import { useEffect, useRef } from "react";
import * as echarts from "echarts";

const DARK_LUMINANCE_THRESHOLD = 0.45;
const LIGHT_LABEL_COLOR = "#E8E8E8";
const DARK_LABEL_COLOR = "#222222";

const parseRgb = (color) => {
  if (typeof color !== "string") return null;
  const hex = color.trim();
  const hexMatch = hex.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (hexMatch) {
    let value = hexMatch[1];
    if (value.length === 3) {
      value = value
        .split("")
        .map((digit) => digit + digit)
        .join("");
    }
    return {
      r: parseInt(value.slice(0, 2), 16),
      g: parseInt(value.slice(2, 4), 16),
      b: parseInt(value.slice(4, 6), 16),
    };
  }
  const rgbMatch = hex.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (!rgbMatch) return null;
  return {
    r: Number(rgbMatch[1]),
    g: Number(rgbMatch[2]),
    b: Number(rgbMatch[3]),
  };
};

export const getTreemapLabelColor = (backgroundColor) => {
  const rgb = parseRgb(backgroundColor);
  if (!rgb) return DARK_LABEL_COLOR;
  const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
  return luminance < DARK_LUMINANCE_THRESHOLD
    ? LIGHT_LABEL_COLOR
    : DARK_LABEL_COLOR;
};

const formatPercent = (value, locale) =>
  Number(value).toLocaleString(locale, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });

export const TreemapChart = ({
  dataChart = [],
  labelsChart = [],
  colorChart = [],
  locale,
}) => {
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);

  useEffect(() => {
    if (!chartRef.current) return;

    if (!chartInstanceRef.current) {
      chartInstanceRef.current = echarts.init(chartRef.current);
    }
    const chart = chartInstanceRef.current;

    const data = (dataChart || []).map((value, index) => {
      const color = colorChart[index] || "#00A7A6";
      return {
        name: labelsChart[index] ?? "",
        value: Number(value) || 0,
        itemStyle: {
          color,
        },
        label: {
          color: getTreemapLabelColor(color),
        },
      };
    });

    chart.setOption(
      {
        tooltip: {
          show: false,
        },
        series: [
          {
            type: "treemap",
            width: "100%",
            height: "100%",
            roam: false,
            nodeClick: false,
            breadcrumb: { show: false },
            data,
            label: {
              show: true,
              formatter: (params) =>
                `${params.name}\n${formatPercent(params.value, locale)}%`,
              color: DARK_LABEL_COLOR,
              fontSize: 12,
              fontWeight: 600,
            },
            itemStyle: {
              borderColor: "#fff",
              borderWidth: 2,
              borderRadius: 8,
              gapWidth: 4,
            },
            levels: [
              {
                itemStyle: {
                  borderWidth: 0,
                  gapWidth: 4,
                },
              },
              {
                itemStyle: {
                  borderColor: "#fff",
                  borderWidth: 2,
                  borderRadius: 8,
                  gapWidth: 4,
                },
              },
            ],
            emphasis: {
              itemStyle: {
                shadowBlur: 0,
              },
            },
          },
        ],
      },
      true,
    );

    const resizeFn = () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.resize();
      }
    };
    window.addEventListener("resize", resizeFn);

    return () => {
      window.removeEventListener("resize", resizeFn);
    };
  }, [colorChart, dataChart, labelsChart, locale]);

  useEffect(() => {
    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.dispose();
        chartInstanceRef.current = null;
      }
    };
  }, []);

  return <div ref={chartRef} style={{ height: "100%", width: "100%" }} />;
};
